/**
 * Story 5.4: Beta User Onboarding - Feedback Modal
 *
 * Modal shown to beta users 7 days after invite to collect feedback
 * Tracks submission via betaFeedbackSubmittedAt to ensure one-time display
 */

import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Star, Send, CheckCircle, AlertCircle } from 'lucide-react';

interface BetaFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
}

interface FeedbackData {
  rating: number;
  comments: string;
  wouldRecommend?: boolean;
  featuresUsed?: string[];
}

export const BetaFeedbackModal: React.FC<BetaFeedbackModalProps> = ({
  open,
  onClose,
  userId
}) => {
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [comments, setComments] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please provide a rating');
      return;
    }

    if (!comments.trim()) {
      setError('Please provide feedback comments');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const feedbackData: FeedbackData = {
        rating,
        comments: comments.trim()
      };

      const response = await fetch('/api/v1/beta/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          userId,
          feedback: feedbackData
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit feedback');
      }

      setSuccess(true);

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Feedback submission error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const getRatingLabel = (value: number): string => {
    switch (value) {
      case 1: return 'Poor - Not meeting expectations';
      case 2: return 'Fair - Some issues';
      case 3: return 'Good - Generally satisfied';
      case 4: return 'Very Good - Mostly exceeds expectations';
      case 5: return 'Excellent - Exceeds all expectations';
      default: return 'Select a rating';
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 " />
        </Transition.Child>

        {/* Modal positioning */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Modal panel */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-[#0C1319] shadow-2xl shadow-black/50 border border-white/[0.06] transition-all">
                {/* Close button */}
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="absolute top-4 right-4 p-2 text-white/40 hover:text-white/60 hover:bg-white/[0.04] rounded-lg transition-colors disabled:opacity-50"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" />
                </button>

                {/* Modal content */}
                <div className="p-6 sm:p-8">
                  {/* Header */}
                  <Dialog.Title as="h3" className="text-xl font-bold text-white mb-1">
                    Beta Program Feedback
                  </Dialog.Title>
                  <p className="text-sm text-white/60 mb-6">
                    Help us improve! Share your experience with the platform so far.
                  </p>

                  {success ? (
                    <div className="flex flex-col items-center py-8">
                      <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                        <CheckCircle className="w-8 h-8 text-green-400" />
                      </div>
                      <p className="text-lg font-semibold text-white text-center">
                        Thank you for your feedback!
                      </p>
                      <p className="text-sm text-white/60 text-center mt-2">
                        Your input helps us make the platform better.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Error Alert */}
                      {error && (
                        <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/20 border border-red-400/30 rounded-lg">
                          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                          <p className="text-sm text-red-300">{error}</p>
                        </div>
                      )}

                      {/* Star Rating */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-white/80 mb-2">
                          Overall Experience *
                        </label>
                        <div className="flex gap-1 mb-2">
                          {[1, 2, 3, 4, 5].map((value) => (
                            <button
                              key={value}
                              type="button"
                              onClick={() => {
                                setRating(value);
                                setError(null);
                              }}
                              onMouseEnter={() => setHoveredRating(value)}
                              onMouseLeave={() => setHoveredRating(0)}
                              className="p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                              disabled={isSubmitting}
                            >
                              <Star
                                className={`w-8 h-8 transition-colors ${
                                  value <= (hoveredRating || rating)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-white/30'
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                        <p className="text-sm text-white/50">
                          {getRatingLabel(hoveredRating || rating)}
                        </p>
                      </div>

                      {/* Comments Textarea */}
                      <div className="mb-6">
                        <label
                          htmlFor="feedback-comments"
                          className="block text-sm font-medium text-white/80 mb-2"
                        >
                          Your Feedback *
                        </label>
                        <textarea
                          id="feedback-comments"
                          rows={6}
                          value={comments}
                          onChange={(e) => {
                            setComments(e.target.value);
                            setError(null);
                          }}
                          placeholder={`Please share your thoughts on:
• What features do you find most valuable?
• What challenges or pain points have you experienced?
• What improvements would you like to see?
• Any bugs or issues you've encountered?`}
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 bg-white/[0.06] border border-white/[0.08] text-white rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none disabled:bg-white/[0.02] disabled:text-white/50 text-sm placeholder-white/30"
                        />
                        <p className="text-xs text-white/50 mt-1">
                          Be as specific as possible - your insights are invaluable
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={handleClose}
                          disabled={isSubmitting}
                          className="flex-1 px-6 py-3 text-base font-semibold text-white/80 bg-white/[0.04] border-2 border-white/[0.08] rounded-lg hover:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-[#0C1319] transition-colors disabled:opacity-50 disabled:cursor-not-allowed order-2 sm:order-1"
                          type="button"
                        >
                          Skip for Now
                        </button>
                        <button
                          onClick={handleSubmit}
                          disabled={isSubmitting || rating === 0 || !comments.trim()}
                          className="flex-1 px-6 py-3 text-base font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 order-1 sm:order-2"
                          type="button"
                        >
                          {isSubmitting ? (
                            <>
                              <svg
                                className="animate-spin h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Send className="w-5 h-5" />
                              Submit Feedback
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

BetaFeedbackModal.displayName = 'BetaFeedbackModal';
